import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import { useLanguage } from "../i18n/LanguageProvider";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://app.wonyaso.com"
).replace(/\/$/, "");

function localizedValue(item, field, language) {
  const preferred = item[`${field}${language === "en" ? "En" : "Fr"}`];
  const fallback = item[`${field}${language === "en" ? "Fr" : "En"}`];
  return preferred || fallback || "";
}

export default function ContentArchive({ section }) {
  const { language } = useLanguage();
  const isTestimonials = section === "testimonials";
  const [state, setState] = useState({
    loading: true,
    content: null,
    error: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (process.env.NODE_ENV === "test") {
      setState({ loading: false, content: null, error: "" });
      return undefined;
    }

    const controller = new AbortController();
    fetch(`${API_BASE_URL}/public/site-content/${section}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Content unavailable");
        return response.json();
      })
      .then((response) => {
        setState({
          loading: false,
          content: response?.data || null,
          error: "",
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setState({
            loading: false,
            content: null,
            error:
              language === "en"
                ? "Unable to load this content."
                : "Impossible de charger ces contenus.",
          });
        }
      });

    return () => controller.abort();
  }, [language, section]);

  const defaultTitle = isTestimonials
    ? language === "en"
      ? "Testimonials"
      : "Témoignages"
    : language === "en"
      ? "Wonya News"
      : "Actualités Wonya";
  const title = state.content
    ? language === "en"
      ? state.content.titleEn || state.content.titleFr
      : state.content.titleFr || state.content.titleEn
    : defaultTitle;
  const items =
    state.content?.enabled && Array.isArray(state.content.items)
      ? [...state.content.items].sort(
          (first, second) =>
            new Date(second.createdAt || 0).getTime() -
            new Date(first.createdAt || 0).getTime(),
        )
      : [];

  return (
    <div className="ws-content-archive">
      <PageMeta
        title={`${title} | WonyaSoft`}
        description={
          isTestimonials
            ? "Découvrez les témoignages publiés par WonyaSoft."
            : "Découvrez les actualités et articles publiés par WonyaSoft."
        }
      />
      <header className="ws-content-archive__hero">
        <div className="ws-shell">
          <span className="ws-kicker">
            {isTestimonials
              ? language === "en"
                ? "CUSTOMER STORIES"
                : "EXPÉRIENCES"
              : language === "en"
                ? "WONYA NEWS"
                : "PUBLICATIONS"}
          </span>
          <h1>{title}</h1>
          <p>
            {isTestimonials
              ? language === "en"
                ? "All published customer experience content."
                : "Tous les contenus d’expérience client publiés."
              : language === "en"
                ? "All WonyaSoft articles, from the most recent to the oldest."
                : "Tous les articles WonyaSoft, du plus récent au plus ancien."}
          </p>
        </div>
      </header>

      <section
        className="ws-shell ws-content-archive__section"
        aria-live="polite"
      >
        {state.loading ? (
          <p>{language === "en" ? "Loading…" : "Chargement…"}</p>
        ) : state.error ? (
          <p role="alert">{state.error}</p>
        ) : items.length === 0 ? (
          <p>
            {language === "en"
              ? "No published content at this time."
              : "Aucun contenu publié pour le moment."}
          </p>
        ) : (
          <>
            <div className="ws-content-archive__summary">
              <span>
                {items.length}{" "}
                {isTestimonials
                  ? language === "en"
                    ? "published stories"
                    : "témoignages publiés"
                  : language === "en"
                    ? "published articles"
                    : "articles publiés"}
              </span>
            </div>
            <div
              className={`ws-content-archive__grid ${
                isTestimonials ? "is-testimonials" : "is-blog"
              }`}
            >
              {items.map((item, index) => {
                const itemTitle = localizedValue(item, "title", language);
                const subtitle = localizedValue(item, "subtitle", language);
                const body = localizedValue(item, "body", language);
                return (
                  <article
                    className={`ws-content-archive__card ${
                      !isTestimonials && index === 0 ? "is-featured" : ""
                    }`}
                    key={item.id}
                  >
                    {item.mediaUrl &&
                      (item.mediaType === "video" ? (
                        <video
                          className="ws-content-archive__media"
                          src={item.mediaUrl}
                          controls
                          preload="metadata"
                          aria-label={itemTitle}
                        />
                      ) : (
                        <img
                          className="ws-content-archive__media"
                          src={item.mediaUrl}
                          alt={itemTitle}
                          loading="lazy"
                          width="1586"
                          height="992"
                        />
                      ))}
                    <div className="ws-content-archive__body">
                      {subtitle && <span>{subtitle}</span>}
                      <h2>{itemTitle}</h2>
                      {isTestimonials ? (
                        <blockquote>{body}</blockquote>
                      ) : (
                        <>
                          <p>{body}</p>
                          <Link to={`/blog/${encodeURIComponent(item.id)}`}>
                            {language === "en"
                              ? "Read article"
                              : "Lire l’article"}
                            <span aria-hidden="true">→</span>
                          </Link>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
